import React, { useCallback, useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { cn } from "@/lib/utils";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  showToast,
  StatusBadge,
} from "@/components/ui";
import { LogViewer } from "@/components/catalog/LogViewer";
import { SyncProgressViewer } from "@/components/catalog/SyncProgressViewer";
import { SyncResultCards } from "@/components/catalog/SyncResultCards";
import {
  AlertTriangle,
  Check,
  Eye,
  FileSpreadsheet,
  Layers,
  Loader2,
  Plus,
  Search,
  Sparkles,
  Trash2,
  Upload,
  X,
} from "lucide-react";

type Marketplace = {
  name?: string;
  logo?: ReactNode;
};

type Props = {
  marketplace?: Marketplace | null;
};

type Suggestion = { type: string; confidence: number };

type FileEntry = {
  id: string;
  name: string;
  size: number;
  rows: number;
  productType: string | null;
  suggestion: Suggestion | null;
  status: "Ready";
  progress: number;
};

type StepStatus = "pending" | "in_progress" | "completed";
type Step = { id: number; name: string; status: StepStatus };

/* ─── Product type catalog ─── */
const productTypeCatalog: string[] = [
  "T-Shirts",
  "Men's T-Shirts",
  "Women's T-Shirts",
  "Graphic T-Shirts",
  "Shirts",
  "Men's Shirts",
  "Women's Shirts",
  "Formal Shirts",
  "Jeans",
  "Men's Jeans",
  "Women's Jeans",
  "Skinny Jeans",
  "Trousers",
  "Chinos",
  "Cargo Pants",
  "Track Pants",
  "Dresses",
  "Casual Dresses",
  "Party Dresses",
  "Maxi Dresses",
  "Kurtas",
  "Men's Kurtas",
  "Women's Kurtas",
  "Ethnic Sets",
  "Jackets",
  "Winter Jackets",
  "Denim Jackets",
  "Bomber Jackets",
  "Shoes",
  "Sneakers",
  "Formal Shoes",
  "Running Shoes",
  "Sandals",
  "Accessories",
  "Watches",
  "Sunglasses",
  "Belts",
  "Wallets",
  "Electronics",
  "Headphones",
  "Earbuds",
  "Speakers",
  "Chargers",
  "Home & Kitchen",
  "Cookware",
  "Storage",
  "Bedsheets",
  "Curtains",
  "Bags",
  "Backpacks",
  "Handbags",
  "Laptop Bags",
  "Travel Bags",
];

/* ─── Auto-suggest based on filename ─── */
const suggestProductType = (filename: string): Suggestion | null => {
  const lower = filename.toLowerCase();
  const hints: Array<{ keywords: string[]; type: string; conf: number }> = [
    { keywords: ["shirt"], type: "Shirts", conf: 88 },
    { keywords: ["tshirt", "t-shirt", "t_shirt"], type: "T-Shirts", conf: 92 },
    { keywords: ["jeans", "denim"], type: "Jeans", conf: 90 },
    { keywords: ["kurta"], type: "Kurtas", conf: 91 },
    { keywords: ["dress"], type: "Dresses", conf: 86 },
    { keywords: ["jacket"], type: "Jackets", conf: 89 },
    { keywords: ["shoe", "sneaker", "footwear"], type: "Shoes", conf: 87 },
    { keywords: ["watch"], type: "Watches", conf: 85 },
    { keywords: ["bag", "backpack"], type: "Bags", conf: 84 },
    { keywords: ["earbud", "headphone"], type: "Earbuds", conf: 83 },
    { keywords: ["trouser", "pant"], type: "Trousers", conf: 86 },
    { keywords: ["top"], type: "Women's T-Shirts", conf: 72 },
    { keywords: ["kids", "children"], type: "T-Shirts", conf: 65 },
  ];
  for (const h of hints) {
    if (h.keywords.some((k) => lower.includes(k)))
      return { type: h.type, confidence: h.conf };
  }
  return null;
};

/* ─── File sync pipeline steps ─── */
const fileSyncSteps: Step[] = [
  { id: 1, name: "Parse uploaded files", status: "pending" },
  { id: 2, name: "Convert CSV/XLSX to JSON", status: "pending" },
  { id: 3, name: "Normalize to Canonical", status: "pending" },
  { id: 4, name: "Compare with Canonical", status: "pending" },
  { id: 5, name: "Generate Canonical mappings", status: "pending" },
];

/* ─── ProductTypeSelector ─── */
const ProductTypeSelector = ({
  value,
  onChange,
  fileId,
}: {
  value: string | null;
  onChange: (next: string | null) => void;
  fileId: string;
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as Node | null;
      if (ref.current && target && !ref.current.contains(target))
        setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const results = query.trim()
    ? productTypeCatalog
        .filter((pt) => pt.toLowerCase().includes(query.toLowerCase()))
        .slice(0, 8)
    : productTypeCatalog.slice(0, 8);

  const exactMatch =
    query.trim() &&
    productTypeCatalog.some((pt) => pt.toLowerCase() === query.toLowerCase());

  return (
    <div ref={ref} className="relative">
      {value ? (
        <div className="inline-flex items-center gap-1.5">
          <span className="px-2 py-0.5 text-xs font-medium rounded bg-indigo-50 dark:bg-indigo-950/50 text-indigo-700 dark:text-indigo-400 border border-indigo-200 dark:border-indigo-800">
            {value}
          </span>
          <button
            data-testid={`clear-type-${fileId}`}
            onClick={() => onChange(null)}
            className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
            type="button"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      ) : (
        <button
          data-testid={`select-type-${fileId}`}
          onClick={() => setOpen(true)}
          className="px-2.5 py-1 text-xs rounded-md border border-dashed border-slate-300 dark:border-slate-600 text-slate-500 dark:text-slate-400 hover:border-indigo-400 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
          type="button"
        >
          Select Product Type
        </button>
      )}
      {open && (
        <div
          data-testid={`type-dropdown-${fileId}`}
          className="absolute z-50 top-full mt-1 left-0 w-64 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-xl overflow-hidden"
        >
          <div className="p-2 border-b border-slate-100 dark:border-slate-700">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
              <input
                data-testid={`type-search-${fileId}`}
                autoFocus
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search product types..."
                className="w-full pl-8 pr-3 py-1.5 text-xs rounded-md bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-slate-900 dark:text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              />
            </div>
          </div>
          <div className="max-h-48 overflow-y-auto">
            {results.length > 0 ? (
              results.map((pt) => (
                <button
                  key={pt}
                  data-testid={`type-option-${pt.replace(/[^a-zA-Z]/g, "")}`}
                  onClick={() => {
                    onChange(pt);
                    setOpen(false);
                    setQuery("");
                  }}
                  className="w-full px-3 py-2 text-left text-xs text-slate-700 dark:text-slate-300 hover:bg-indigo-50 dark:hover:bg-indigo-950/40 transition-colors"
                  type="button"
                >
                  {pt}
                </button>
              ))
            ) : !exactMatch && query.trim() ? (
              <div className="p-3 text-center">
                <p className="text-[10px] text-slate-400 mb-2">
                  No match found
                </p>
                <button
                  data-testid={`create-type-${fileId}`}
                  onClick={() => {
                    onChange(query.trim());
                    setOpen(false);
                    setQuery("");
                    showToast.success(
                      "Created",
                      `Product type "${query.trim()}" created.`,
                    );
                  }}
                  className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-950/40 rounded-md hover:bg-indigo-100 dark:hover:bg-indigo-950 transition-colors"
                  type="button"
                >
                  <Plus className="w-3 h-3" />
                  Create "{query.trim()}"
                </button>
              </div>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
};

/* ─── Preview Modal ─── */
const PreviewModal = ({
  file,
  onClose,
}: {
  file: FileEntry | null;
  onClose: () => void;
}) => {
  if (!file) return null;
  const previewRows = [
    ["SKU-001", "Cotton Slim Fit Shirt - Blue", "Blue", "M", "899", "50"],
    ["SKU-002", "Linen Casual Shirt - White", "White", "L", "1299", "35"],
    ["SKU-003", "Denim Western Shirt", "Indigo", "XL", "1499", "22"],
    ["SKU-004", "Oxford Button-Down - Pink", "Pink", "S", "1099", "41"],
    ["SKU-005", "Flannel Check Shirt - Red", "Red", "M", "999", "18"],
  ];
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={onClose}
    >
      <div
        data-testid="file-preview-modal"
        className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl w-full max-w-2xl mx-4 border border-slate-200 dark:border-slate-700"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 dark:border-slate-800">
          <div>
            <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
              {file.name}
            </h3>
            <p className="text-[10px] text-slate-400">First 5 rows preview</p>
          </div>
          <button
            data-testid="close-preview"
            onClick={onClose}
            className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-800"
            type="button"
          >
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>
        <div className="overflow-x-auto p-4">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-slate-200 dark:border-slate-700">
                {["SKU", "Title", "Color", "Size", "Price", "Stock"].map(
                  (h) => (
                    <th
                      key={h}
                      className="px-3 py-2 text-left font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider"
                    >
                      {h}
                    </th>
                  ),
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {previewRows.map((row, i) => (
                <tr key={i}>
                  {row.map((c, j) => (
                    <td
                      key={j}
                      className="px-3 py-2 text-slate-700 dark:text-slate-300"
                    >
                      {c}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export const FileUploadSync: React.FC<Props> = ({ marketplace }) => {
  const [files, setFiles] = useState<FileEntry[]>([]);
  const [dragging, setDragging] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncDone, setSyncDone] = useState(false);
  const [steps, setSteps] = useState<Step[]>(
    fileSyncSteps.map((s) => ({ ...s })),
  );
  const [syncLogs, setSyncLogs] = useState<
    Array<{
      id: number;
      timestamp: string;
      level: "info" | "success" | "warning" | "error";
      message: string;
    }>
  >([]);
  const [previewFile, setPreviewFile] = useState<FileEntry | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const timerRef = useRef<number | null>(null);
  const logIdRef = useRef(0);

  useEffect(() => {
    return () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
    };
  }, []);

  const mockRows = (name: string) => {
    let s = 0;
    for (let i = 0; i < name.length; i++) s += name.charCodeAt(i);
    return 200 + (s % 2000);
  };

  const addFiles = useCallback((newFiles: File[]) => {
    const validExts = [".csv", ".xlsx", ".xls"];
    const maxSize = 50 * 1024 * 1024;
    const entries: FileEntry[] = [];
    for (const f of newFiles) {
      const ext = `.${f.name.split(".").pop()?.toLowerCase() || ""}`;
      if (!validExts.includes(ext)) {
        showToast.error("Invalid file", `${f.name} — only CSV/XLSX supported.`);
        continue;
      }
      if (f.size > maxSize) {
        showToast.error("File too large", `${f.name} exceeds 50MB limit.`);
        continue;
      }
      const suggestion = suggestProductType(f.name);
      entries.push({
        id: `file-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        name: f.name,
        size: f.size || Math.floor(Math.random() * 5000000),
        rows: mockRows(f.name),
        productType: suggestion?.type || null,
        suggestion,
        status: "Ready",
        progress: 100,
      });
    }
    if (entries.length > 0) {
      setFiles((prev) => [...prev, ...entries]);
      showToast.success("Uploaded", `${entries.length} file(s) added.`);
    }
  }, []);

  const onDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(true);
  };
  const onDragLeave = () => setDragging(false);
  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    addFiles(Array.from(e.dataTransfer.files));
  };
  const onBrowse = () => inputRef.current?.click();
  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const list = e.target.files ? Array.from(e.target.files) : [];
    addFiles(list);
    e.target.value = "";
  };

  const removeFile = (id: string) =>
    setFiles((prev) => prev.filter((f) => f.id !== id));
  const setProductType = (id: string, type: string | null) =>
    setFiles((prev) =>
      prev.map((f) => (f.id === id ? { ...f, productType: type } : f)),
    );

  const [bulkSelection, setBulkSelection] = useState<Set<string>>(new Set());
  const toggleBulk = (id: string) =>
    setBulkSelection((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  const [bulkType, setBulkType] = useState<string | null>(null);
  const applyBulkType = () => {
    if (!bulkType || bulkSelection.size === 0) return;
    setFiles((prev) =>
      prev.map((f) =>
        bulkSelection.has(f.id) ? { ...f, productType: bulkType } : f,
      ),
    );
    showToast.success(
      "Bulk assigned",
      `${bulkSelection.size} files set to "${bulkType}".`,
    );
    setBulkSelection(new Set());
    setBulkType(null);
  };

  const totalRows = files.reduce((s, f) => s + f.rows, 0);
  const readyCount = files.filter((f) => f.productType).length;
  const missingType = files.filter((f) => !f.productType).length;
  const canSync = files.length > 0 && missingType === 0 && !syncing;

  const addLog = (
    message: string,
    level: "info" | "success" | "warning" | "error" = "info",
  ) => {
    logIdRef.current += 1;
    setSyncLogs((prev) => [
      ...prev,
      {
        id: logIdRef.current,
        timestamp: new Date().toISOString(),
        level,
        message,
      },
    ]);
  };

  const startSync = () => {
    setSyncing(true);
    setSyncDone(false);
    setSyncLogs([]);
    logIdRef.current = 0;
    setSteps(fileSyncSteps.map((s) => ({ ...s, status: "pending" })));
    let i = 0;
    const advance = () => {
      if (i < fileSyncSteps.length) {
        const step = fileSyncSteps[i];
        addLog(`${step.name}...`);
        setSteps((prev) =>
          prev.map((s, idx) =>
            idx === i
              ? { ...s, status: "in_progress" }
              : idx < i
                ? { ...s, status: "completed" }
                : s,
          ),
        );
        i++;
        timerRef.current = window.setTimeout(advance, 900);
      } else {
        setSteps((prev) => prev.map((s) => ({ ...s, status: "completed" })));
        addLog(
          `${totalRows.toLocaleString()} products processed successfully.`,
          "success",
        );
        setSyncing(false);
        setSyncDone(true);
        showToast.success(
          "Sync complete",
          `${totalRows.toLocaleString()} products processed via file upload.`,
        );
      }
    };
    advance();
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-3">
              <span className="text-2xl">{marketplace?.logo}</span>
              File Upload Sync — {marketplace?.name}
            </CardTitle>
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-medium rounded-full bg-amber-50 dark:bg-amber-950/50 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-800">
              <FileSpreadsheet className="w-3 h-3" />
              File-Based Sync
            </span>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4 p-4 rounded-lg bg-slate-50 dark:bg-slate-800/50">
            <div>
              <p className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1">
                Canonical Model
              </p>
              <p className="text-sm font-medium text-slate-900 dark:text-slate-100 flex items-center gap-2">
                <Layers className="w-4 h-4 text-indigo-500" />
                SmartStore
              </p>
            </div>
            <div>
              <p className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1">
                Sync Mode
              </p>
              <p className="text-sm font-medium text-amber-600 dark:text-amber-400">
                Manual File Upload
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Upload Product Files</CardTitle>
        </CardHeader>
        <CardContent>
          <div
            data-testid="upload-dropzone"
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
            className={cn(
              "flex flex-col items-center justify-center gap-3 p-10 rounded-xl border-2 border-dashed transition-all cursor-pointer",
              dragging
                ? "border-indigo-500 bg-indigo-50/50 dark:bg-indigo-950/30"
                : "border-slate-300 dark:border-slate-600 hover:border-indigo-400 dark:hover:border-indigo-600 bg-slate-50/50 dark:bg-slate-800/30",
            )}
            onClick={onBrowse}
          >
            <div
              className={cn(
                "p-3 rounded-xl",
                dragging
                  ? "bg-indigo-100 dark:bg-indigo-900"
                  : "bg-slate-100 dark:bg-slate-800",
              )}
            >
              <Upload
                className={cn(
                  "w-7 h-7",
                  dragging ? "text-indigo-500" : "text-slate-400",
                )}
              />
            </div>
            <div className="text-center">
              <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
                {dragging
                  ? "Drop files here"
                  : "Drag & drop CSV or Excel files here"}
              </p>
              <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">
                or
              </p>
            </div>
            <Button
              data-testid="browse-files-btn"
              variant="outline"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                onBrowse();
              }}
            >
              Browse Files
            </Button>
            <p className="text-[10px] text-slate-400 dark:text-slate-500">
              Supported: CSV, XLSX &middot; Max 50MB per file
            </p>
            <input
              ref={inputRef}
              type="file"
              multiple
              accept=".csv,.xlsx,.xls"
              className="hidden"
              onChange={onInputChange}
            />
          </div>
        </CardContent>
      </Card>

      {files.length > 0 && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">
                Uploaded Files ({files.length})
              </CardTitle>
              {bulkSelection.size > 0 && (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-500 dark:text-slate-400">
                    {bulkSelection.size} selected
                  </span>
                  <div className="w-40">
                    <ProductTypeSelector
                      value={bulkType}
                      onChange={setBulkType}
                      fileId="bulk"
                    />
                  </div>
                  <Button
                    data-testid="apply-bulk-type"
                    variant="outline"
                    size="sm"
                    disabled={!bulkType}
                    onClick={applyBulkType}
                  >
                    <Check className="w-3.5 h-3.5" />
                    Apply
                  </Button>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <table data-testid="files-table" className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 dark:border-slate-700">
                  <th className="px-4 py-2.5 w-10">
                    <input
                      type="checkbox"
                      className="rounded border-slate-300 dark:border-slate-600 text-indigo-600 focus:ring-indigo-500/20 h-3.5 w-3.5"
                      checked={
                        bulkSelection.size === files.length && files.length > 0
                      }
                      onChange={() => {
                        if (bulkSelection.size === files.length)
                          setBulkSelection(new Set());
                        else setBulkSelection(new Set(files.map((f) => f.id)));
                      }}
                    />
                  </th>
                  {[
                    "File Name",
                    "Rows",
                    "Product Type",
                    "Status",
                    "Actions",
                  ].map((h) => (
                    <th
                      key={h}
                      className="px-4 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {files.map((f) => (
                  <tr
                    key={f.id}
                    data-testid={`file-row-${f.id}`}
                    className="hover:bg-slate-50 dark:hover:bg-slate-800/50"
                  >
                    <td className="px-4 py-2.5">
                      <input
                        type="checkbox"
                        checked={bulkSelection.has(f.id)}
                        onChange={() => toggleBulk(f.id)}
                        className="rounded border-slate-300 dark:border-slate-600 text-indigo-600 focus:ring-indigo-500/20 h-3.5 w-3.5"
                      />
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-2">
                        <FileSpreadsheet className="w-4 h-4 text-emerald-500 shrink-0" />
                        <div>
                          <p className="text-sm font-medium text-slate-900 dark:text-slate-100">
                            {f.name}
                          </p>
                          <p className="text-[10px] text-slate-400">
                            {(f.size / 1024).toFixed(0)} KB
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-2.5 text-slate-700 dark:text-slate-300">
                      {f.rows.toLocaleString()} rows
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="space-y-1">
                        <ProductTypeSelector
                          value={f.productType}
                          onChange={(type) => setProductType(f.id, type)}
                          fileId={f.id}
                        />
                        {!f.productType && f.suggestion && (
                          <button
                            data-testid={`accept-suggestion-${f.id}`}
                            onClick={() =>
                              setProductType(f.id, f.suggestion!.type)
                            }
                            className="inline-flex items-center gap-1 text-[10px] text-indigo-600 dark:text-indigo-400 hover:underline"
                            type="button"
                          >
                            <Sparkles className="w-3 h-3" />
                            Suggested: {f.suggestion.type} (
                            {f.suggestion.confidence}%)
                          </button>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-2.5">
                      {f.productType ? (
                        <StatusBadge status="ACTIVE" showDot>
                          Ready
                        </StatusBadge>
                      ) : (
                        <StatusBadge status="WARNING" showDot>
                          Needs Type
                        </StatusBadge>
                      )}
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-1">
                        <button
                          data-testid={`preview-${f.id}`}
                          onClick={() => setPreviewFile(f)}
                          title="Preview"
                          className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500 hover:text-indigo-600 dark:hover:text-indigo-400"
                          type="button"
                        >
                          <Eye className="w-3.5 h-3.5" />
                        </button>
                        <button
                          data-testid={`delete-${f.id}`}
                          onClick={() => removeFile(f.id)}
                          title="Delete"
                          className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500 hover:text-rose-600 dark:hover:text-rose-400"
                          type="button"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {files.length > 0 && (
        <Card data-testid="validation-summary">
          <CardHeader>
            <CardTitle className="text-base">Validation Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50">
                <p className="text-2xl font-bold text-slate-900 dark:text-slate-100">
                  {files.length}
                </p>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  Files Uploaded
                </p>
              </div>
              <div className="p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50">
                <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">
                  {readyCount}
                </p>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  Files Ready
                </p>
              </div>
              <div className="p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50">
                <p className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">
                  {totalRows.toLocaleString()}
                </p>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  Total Products Detected
                </p>
              </div>
            </div>

            {missingType > 0 && (
              <div
                data-testid="validation-warning"
                className="flex items-center gap-2 p-3 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 text-sm text-amber-700 dark:text-amber-400 mb-4"
              >
                <AlertTriangle className="w-4 h-4 shrink-0" />
                {missingType} file{missingType > 1 ? "s" : ""} missing product
                type
              </div>
            )}

            <Button
              data-testid="start-file-sync-btn"
              className="w-full"
              disabled={!canSync}
              onClick={startSync}
            >
              {syncing ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Upload className="w-4 h-4" />
              )}
              {syncing ? "Processing..." : "Start Catalog Sync"}
            </Button>
          </CardContent>
        </Card>
      )}

      {(syncing || syncDone) && (
        <Card>
          <CardHeader>
            <CardTitle>Sync Progress</CardTitle>
          </CardHeader>
          <CardContent>
            <SyncProgressViewer steps={steps} />
          </CardContent>
        </Card>
      )}

      {(syncing || syncDone) && (
        <Card>
          <CardContent className="p-0">
            <LogViewer logs={syncLogs} maxHeight="250px" />
          </CardContent>
        </Card>
      )}

      {syncDone && (
        <div>
          <h3 className="text-sm font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-3">
            Sync Results Summary
          </h3>
          <SyncResultCards
            results={{
              imported: totalRows,
              duplicates: 0,
              mappings: readyCount,
              needsReview: 0,
            }}
          />
        </div>
      )}

      <PreviewModal file={previewFile} onClose={() => setPreviewFile(null)} />
    </div>
  );
};
